import { BottomNavigation, BottomNavigationProps } from "@mui/material";
import React, { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ProfileIcon } from "~/src/components/Icons/Icons";
import AppDataContext from "~/src/contexts/AppDataContext/DataProvider";
import { useStateful } from "~/src/hooks/useStateful";
import NavigationButtonDefault from "./NavigationButtonDefault";

export type NavigationDefaultProps = Omit<BottomNavigationProps, "ref" | "children"> & {
  id: string | number;
  ref?: React.Ref<HTMLDivElement>;
};

const NavigationDefault: React.ForwardRefRenderFunction<HTMLDivElement, NavigationDefaultProps> = (
  { id, ...rest },
  ref,
) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const appData = useContext(AppDataContext);
  const hasJaktomrader = appData.jaktområder.data && appData.jaktområder.data.length > 0;

  const otherRoutes = [
    {
      id: "mainPageNavigationButtonOversikt",
      label: <>{t("pages.oversikt.title")}</>,
      icon: (
        <div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            role="graphics-symbol"
          >
            <path
              d="M13.3333 8V0H24V8H13.3333ZM0 13.3333V0H10.6667V13.3333H0ZM13.3333 24V10.6667H24V24H13.3333ZM0 24V16H10.6667V24H0ZM2.66667 10.6667H8V2.66667H2.66667V10.6667ZM16 21.3333H21.3333V13.3333H16V21.3333ZM16 5.33333H21.3333V2.66667H16V5.33333ZM2.66667 21.3333H8V18.6667H2.66667V21.3333Z"
              fill="currentColor"
            />
          </svg>
        </div>
      ),
      path: "/oversikt",
    },
    {
      id: "mainPageNavigationButtonMinSide",
      label: <>{t("pages.minSide.profil")}</>,
      icon: <ProfileIcon />,
      path: "/minside",
    },
  ];

  const nyRegistreringRoute = [
    {
      id: "mainPageNavigationButtonNyRegistrering",
      label: <RegistreringTitle />,
      icon: (
        <svg
          role="graphics-symbol"
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M10.8 18H13.2V13.2H18V10.8H13.2V6H10.8V10.8H6V13.2H10.8V18ZM12 24C10.34 24 8.78 23.685 7.32 23.055C5.86 22.425 4.59 21.57 3.51 20.49C2.43 19.41 1.575 18.14 0.945 16.68C0.315 15.22 0 13.66 0 12C0 10.34 0.315 8.78 0.945 7.32C1.575 5.86 2.43 4.59 3.51 3.51C4.59 2.43 5.86 1.575 7.32 0.945C8.78 0.315 10.34 0 12 0C13.66 0 15.22 0.315 16.68 0.945C18.14 1.575 19.41 2.43 20.49 3.51C21.57 4.59 22.425 5.86 23.055 7.32C23.685 8.78 24 10.34 24 12C24 13.66 23.685 15.22 23.055 16.68C22.425 18.14 21.57 19.41 20.49 20.49C19.41 21.57 18.14 22.425 16.68 23.055C15.22 23.685 13.66 24 12 24ZM12 21.6C14.68 21.6 16.95 20.67 18.81 18.81C20.67 16.95 21.6 14.68 21.6 12C21.6 9.32 20.67 7.05 18.81 5.19C16.95 3.33 14.68 2.4 12 2.4C9.32 2.4 7.05 3.33 5.19 5.19C3.33 7.05 2.4 9.32 2.4 12C2.4 14.68 3.33 16.95 5.19 18.81C7.05 20.67 9.32 21.6 12 21.6Z" />
        </svg>
      ),
      path: "/registrering",
    },
  ];

  function RegistreringTitle() {
    const width = useStateful(window.innerWidth);

    useEffect(() => {
      const handleResize = () => width.set(window.innerWidth);
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);

    return <>{width.value < 400 ? "Registrering" : "Ny registrering"}</>;
  }

  const navbarRoutes = !hasJaktomrader ? otherRoutes : nyRegistreringRoute.concat(otherRoutes);

  return (
    <BottomNavigation
      id="mainPageNavigation"
      component={"nav"}
      ref={ref}
      data-testid={id}
      className={"w-full h-20 bg-ldir-blue-900"}
      showLabels={true}
      onChange={(_event, selectedIndex) => {
        navigate(navbarRoutes[selectedIndex].path);
      }}
      {...rest}
    >
      {navbarRoutes.map((button, _index) => {
        return <NavigationButtonDefault key={_index} {...button} />;
      })}
    </BottomNavigation>
  );
};

export default React.forwardRef(NavigationDefault);
